import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Box,
  Button,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  ChecklistInvalidItem,
  type ChecklistRow,
} from "Components/Merchant/OrbitL/ChecklistInvalidItem";
import {
  type BankEnvironment,
  type OrbitLChecklistDto,
  OrbitLChecklistIssue,
} from "Infrastructure/Api/Api";
import { appUrl } from "Utils/UrlUtils";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Components.Merchant.OrbitL.Checklist;

type OrbitLChecklistProps = {
  checklist: OrbitLChecklistDto;
  isSelfEmployed: boolean;
};

export const OrbitLChecklist = ({
  checklist,
  isSelfEmployed,
}: OrbitLChecklistProps) => {
  const { t } = useResource();

  const executiveInfoLabel = isSelfEmployed
    ? t(RESOURCES.Items.ExecutiveInfoSelfEmployed)
    : t(RESOURCES.Items.ExecutiveInfo);
  const executiveDocsLabel = isSelfEmployed
    ? t(RESOURCES.Items.ExecutiveDocumentsSelfEmployed)
    : t(RESOURCES.Items.ExecutiveDocuments);
  const executiveInfoLink = isSelfEmployed
    ? `${appUrl("user-account")}?activeTab=party`
    : `${appUrl("user-account")}?activeTab=relatedParties`;
  const executiveDocsLink = isSelfEmployed
    ? `${appUrl("user-account")}?activeTab=party`
    : `${appUrl("user-account")}?activeTab=relatedParties`;

  const executiveValidation = isSelfEmployed
    ? t(RESOURCES.Validation.ExecutiveInfoSelfEmployed)
    : t(RESOURCES.Validation.ExecutiveInfo);
  const executiveDocsValidation = isSelfEmployed
    ? t(RESOURCES.Validation.ExecutiveDocumentsSelfEmployed)
    : t(RESOURCES.Validation.ExecutiveDocuments);

  // A missing account has to be added in settings; a missing ownership proof only needs the bank
  // reconnected, which is a different action and the only case the button belongs to.
  const bankIssues = checklist.bankAccountInfo.issues ?? [];
  const isBankReconnectOnly =
    bankIssues.length > 0 &&
    bankIssues.every(
      (issue) => issue === OrbitLChecklistIssue.BankVerificationAudit,
    );

  const items: Array<ChecklistRow> = [
    {
      label: t(RESOURCES.Items.BasicInfo),
      item: checklist.basicInfo,
      validation: t(RESOURCES.Validation.BasicInfo),
      link: null,
    },
    {
      label: t(RESOURCES.Items.FacilityInfo),
      item: checklist.facilityInfo,
      validation: t(RESOURCES.Validation.FacilityInfo),
      link: `${appUrl("user-account")}?activeTab=company`,
    },
    {
      label: t(RESOURCES.Items.BankAccountInfo),
      item: checklist.bankAccountInfo,
      validation: isBankReconnectOnly
        ? t(RESOURCES.BankReconnect.Explanation)
        : t(RESOURCES.Validation.BankAccountInfo),
      link: isBankReconnectOnly ? null : appUrl("bank-address-list"),
      action: isBankReconnectOnly ? (
        <BankReconnectButton
          environment={checklist.primaryBankAccountEnvironment}
        />
      ) : null,
      // The explanation above already names the gap — the code bullet would only repeat it.
      areIssuesHidden: isBankReconnectOnly,
    },
    {
      label: executiveInfoLabel,
      item: checklist.executiveInfo,
      validation: executiveValidation,
      link: executiveInfoLink,
    },
    {
      label: executiveDocsLabel,
      item: checklist.executiveDocuments,
      validation: executiveDocsValidation,
      link: executiveDocsLink,
    },
    {
      label: t(RESOURCES.Items.TerminalAssignment),
      item: checklist.terminalAssignment,
      validation: t(RESOURCES.Validation.TerminalAssignment),
      link: null,
    },
  ];

  const validItems = items.filter((i) => i.item.isValid);
  const invalidItems = items.filter((i) => !i.item.isValid);

  if (checklist.isComplete) {
    return (
      <Stack gap={2}>
        <Typography variant="h6">{t(RESOURCES.Heading)}</Typography>
        <Alert severity="success">{t(RESOURCES.CompletionSuccess)}</Alert>
      </Stack>
    );
  }

  return (
    <Stack gap={1}>
      <Typography variant="h6">{t(RESOURCES.Heading)}</Typography>

      {/* Completed items — just show green checks */}
      {validItems.length > 0 && (
        <List disablePadding>
          {validItems.map((item) => (
            <ListItem key={item.label} sx={{ py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <CheckCircleIcon color="success" />
              </ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItem>
          ))}
        </List>
      )}

      {/* Incomplete items — collapsed accordion with soft warning color */}
      {invalidItems.length > 0 && (
        <Accordion
          defaultExpanded={false}
          sx={{
            "&::before": { display: "none" },
            boxShadow: "none",
            border: "1px solid",
            borderColor: "warning.light",
            borderRadius: "8px !important",
            bgcolor: "warning.50",
          }}
          disableGutters
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              bgcolor: "warning.50",
              borderRadius: "8px",
              minHeight: 48,
              "&.Mui-expanded": { minHeight: 48 },
            }}
          >
            <InfoOutlinedIcon sx={{ color: "warning.main", mr: 1 }} />
            <Typography variant="body2" fontWeight={500}>
              {t(RESOURCES.ToCompleteSection)}
            </Typography>
            <Chip
              label={invalidItems.length}
              size="small"
              sx={{
                ml: 1,
                bgcolor: "warning.main",
                color: "white",
                height: 22,
              }}
            />
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 1 }}>
            <List disablePadding>
              {invalidItems.map((row) => (
                <ChecklistInvalidItem key={row.label} {...row} />
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      )}
    </Stack>
  );
};

type BankReconnectButtonProps = {
  environment?: BankEnvironment | null;
};

/**
 * Sends the merchant straight into the consent flow for the bank their payout account is at. Without
 * a known environment they land on the bank picker, which is still one click closer than settings.
 */
const BankReconnectButton = ({ environment }: BankReconnectButtonProps) => {
  const { t } = useResource();

  const url = environment
    ? `${appUrl("banking-auth")}?environment=${encodeURIComponent(environment)}`
    : appUrl("banking-auth");

  return (
    <Box mt={1}>
      <Button variant="outlined" size="small" href={url}>
        {t(RESOURCES.BankReconnect.Button)}
      </Button>
    </Box>
  );
};
